import { doc, collection, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { canEditStudent } from '@/config/roles'
import {
  RULE_EXPECTATIONS,
  attempt,
  accessDetail,
  fmtExpected,
  probe
} from '../ruleExpectations.js'

// Live check that SERVER edit rules agree with the CLIENT's canEditStudent()
// for the current role: creates two tagged smoke students — one on your
// caseload, one on someone else's — and attempts to update each. The client
// function is the expectation; the live rules are the system under test, so
// drift between UI ("you can edit this") and server ("denied") is caught.
//
// Role-aware: if the role cannot create students, the create denial is asserted
// as a pass and the edit probes are skipped (no doc to edit).
export default {
  id: 'studentEditScope',
  label: 'Student edit scope (own vs other caseload)',
  description: 'Server edit rules must agree with the client canEditStudent() for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const currentUser = { uid: ctx.user.uid, role }

    const makeDoc = (caseManagerId) => ({
      app: {
        studentData: {
          firstName: 'SMOKE',
          lastName: 'EditScope',
          plan: 'IEP',
          caseManagerId
        },
        accommodations: { instruction: '', assessment: '' },
        schedule: { classServices: [] },
        staffIds: []
      },
      __smokeTest: true,
      __smokeRunId: ctx.runId,
      createdAt: serverTimestamp(),
      ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
    })

    const ownRef = doc(collection(db, 'students'))
    const otherRef = doc(collection(db, 'students'))
    const expectCreate = RULE_EXPECTATIONS.studentCreate(role)
    let created = false

    try {
      await step(
        `create own+other smoke students (expected: ${fmtExpected(expectCreate)})`,
        async () => {
          const { allowed } = await attempt(async () => {
            await setDoc(ownRef, makeDoc(ctx.user.uid))
            await setDoc(otherRef, makeDoc('smoke-other-cm'))
          })
          created = allowed
          return allowed
            ? `${accessDetail(expectCreate, allowed)} — students/${ownRef.id}, students/${otherRef.id}`
            : accessDetail(expectCreate, allowed)
        }
      )

      // Can't probe edit scope without test docs.
      if (!created) {
        step.skip('own vs other edit probes', 'Role cannot create — later steps skipped')
        return
      }

      for (const [name, ref, caseManagerId] of [
        ['own-caseload', ownRef, ctx.user.uid],
        ['other-caseload', otherRef, 'smoke-other-cm']
      ]) {
        const clientSays = canEditStudent(
          { caseManagerId, app: { studentData: { caseManagerId, plan: 'IEP' } } },
          currentUser
        )
        await step(
          `edit ${name} student (client says: ${clientSays ? 'editable' : 'not editable'})`,
          () =>
            probe(clientSays, async () => {
              await setDoc(ref, { app: { studentData: { grade: '11' } } }, { merge: true })
            })
        )
      }
    } finally {
      // Delete needs super-admin tier; leftovers are swept by the panel's
      // cleanup tool if this role can't delete.
      await deleteDoc(ownRef).catch(() => {})
      await deleteDoc(otherRef).catch(() => {})
    }
  }
}
